"use client";

import { useState } from "react";

import Image from "next/image";

import { Button } from "@/app/_components/shared/button";
import {
  PLATFORM_FEATURES_BY_TAB,
  PLATFORM_FEATURES_DEFAULT_TAB_ID,
  PLATFORM_FEATURES_SECTION,
  PLATFORM_FEATURE_TABS,
  type PlatformFeatureTabId,
} from "@/app/_libs/data/landing-page/platform-features-section";

export function PlatformFeaturesSection() {
  const [activeTab, setActiveTab] = useState<PlatformFeatureTabId>(
    PLATFORM_FEATURES_DEFAULT_TAB_ID,
  );

  return (
    <section
      id="features"
      className="relative flex w-full scroll-mt-20 flex-col items-center justify-center gap-10 bg-white px-4 py-12 sm:px-6 md:gap-16 md:px-[6%] md:py-20 lg:px-[7%]">
      <div className="relative flex w-full flex-col items-center gap-4 md:gap-6">
        <h2 className="relative text-center text-2xl leading-normal font-bold tracking-[0.2px] text-black md:text-3xl">
          {PLATFORM_FEATURES_SECTION.heading}
        </h2>
      </div>
      <div className="relative flex w-full flex-col items-center justify-center gap-8 md:gap-9">
        <div className="relative flex w-full items-center justify-start overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] lg:justify-center xl:overflow-visible [&::-webkit-scrollbar]:hidden">
          <div className="flex h-[45px] w-max gap-6 px-1 md:gap-8 md:px-0">
            {PLATFORM_FEATURE_TABS.map((tab) => {
              const isActive = tab.id === activeTab;

              return (
                <div
                  key={tab.id}
                  className={`relative flex h-full items-center justify-center px-0 pt-0 pb-2.5 lg:gap-2.5 ${isActive ? "border-b-[3px] border-black" : ""}`}>
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative min-h-px min-w-px flex-1 cursor-pointer text-center text-xs leading-normal font-medium whitespace-nowrap text-black md:text-sm lg:text-base ${isActive ? "font-bold" : "opacity-60"}`}>
                    {tab.label}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-background-50 md:6 relative flex w-[90vw] flex-col items-center justify-between gap-10 rounded-lg p-4 lg:w-full lg:max-w-[100vw] lg:flex-row lg:gap-0 lg:p-8">
          <div className="relative order-2 flex w-full flex-col items-start lg:order-1 lg:w-[60vw] lg:gap-18 xl:gap-8">
            {PLATFORM_FEATURES_BY_TAB[activeTab].map((feature, index) => (
              <div
                key={index}
                className="relative flex w-full flex-col items-start justify-center gap-2.5">
                <div className="flex size-7 items-center justify-center rounded-full border-8 border-black bg-black md:size-9 lg:size-11">
                  <div className="flex size-4 items-center justify-center text-white">
                    <Image
                      src="/images/notes.png"
                      alt="Notes"
                      width={12}
                      height={12}
                      className="h-3 w-5 md:h-4 md:w-5 lg:h-4.5 lg:w-10"
                    />
                  </div>
                </div>
                <div className="relative flex h-[25vw] w-full flex-col items-start gap-2 md:h-[15vw] md:gap-2.5 lg:h-[7vw]">
                  <div className="relative flex w-full flex-col items-start gap-2">
                    <p className="text-typography-700 relative w-full text-base leading-normal font-bold tracking-[0.2px] md:text-lg lg:text-xl">
                      {feature.title}
                    </p>
                  </div>
                  <p className="text-typography-800 relative w-full text-xs leading-[1.5] font-normal md:text-sm lg:text-lg">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="relative order-1 mt-6 h-[320px] w-full sm:h-[420px] lg:order-2 lg:mt-0 lg:h-[578px] lg:w-[40vw]">
            <Image
              src="/images/features.png"
              alt="Mobile App"
              fill
              className="object-contain"
              sizes="(max-width: 1024px) 100vw, 40vw"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
